import { useGT } from 'gt-react';
import type { PageKey } from '@erp/rpc';
import { useAgentModule } from '@/hooks/useAgentModule';
import type { Message, MessageToolResult } from '@/context/agent-state.context';
import { renderSharedToolResult } from '@/components/common/chat/useChatToolResultRenderer';
import { useNavigablePages } from '@/components/nami/useNavigablePages';

interface EdiAgentModuleOptions {
  /** Which EDI page this is — resolved against the backend page registry. */
  pageKey: PageKey;
  /** Route path, shown to the agent as the current location. */
  currentPage: string;
  /** Input placeholder, phrased as a question this page can answer. */
  placeholder: string;
  /** Live on-screen state (filters, selection) for "these orders" to resolve to. */
  getModuleContext?: () => Record<string, unknown> | undefined;
}

/**
 * Register a page with Nami's `edi` module.
 *
 * Wraps `useAgentModule` so the four EDI pages don't each repeat the module key,
 * the panel title, and the generative-UI renderer — they differ only in which
 * page key they claim and what on-screen state they expose. Registering is what
 * makes the Nami sidebar trigger active on a page; without it the trigger renders
 * disabled.
 *
 * The shared renderer displays views and harness download cards on EDI pages.
 */
export function useEdiAgentModule({
  pageKey,
  currentPage,
  placeholder,
  getModuleContext,
}: EdiAgentModuleOptions): void {
  const gt = useGT();
  const pages = useNavigablePages();

  // Not memoized: the React Compiler handles that, and `useAgentModule` stores
  // this in a ref anyway, so a fresh identity per render never re-registers.
  function renderToolResult(_message: Message, toolResult: MessageToolResult) {
    // Everything without a card falls through to the message list's default.
    return renderSharedToolResult(toolResult, pages) ?? undefined;
  }

  useAgentModule({
    moduleKey: 'edi',
    pageKey,
    currentPage,
    placeholder,
    headerTitle: gt('EDI Assistant'),
    getModuleContext,
    renderToolResult,
  });
}
